<template>
  <div class="signature-capture-container rounded-lg">
    <div class="signature-header">
      <h2>{{ title }}</h2>
      <p v-if="subtitle" class="subtitle">{{ subtitle }}</p>
    </div>

    <div class="sign-here-label">
      <span class="sign-here-text">SIGN HERE</span>
      <i class="pi pi-chevron-down chevron-icon"></i>
    </div>
    <div class="signature-pad-container bg-gray-200">
      <VueSignaturePad ref="signaturePad" :width="width" :height="height" :options="options" />
    </div>

    <div class="signature-actions">
      <div class="action-left">
        <div v-if="legalDeclaration" class="declaration-checkbox">
          <Checkbox v-model="declarationAgreed" :binary="true" inputId="declaration-agreement" />
          <label for="declaration-agreement">I have read and agree to the statement below</label>
        </div>
      </div>
      <div class="action-right">
        <Button label="Clear" icon="pi pi-refresh" class="p-button-outlined mr-2" @click="clear" />
        <Button
          label="Cancel"
          icon="pi pi-times"
          class="p-button-outlined p-button-danger mr-2"
          @click="cancel"
        />
        <Button
          label="Save Signature"
          icon="pi pi-check"
          @click="save"
          :disabled="!!legalDeclaration && !declarationAgreed"
        />
      </div>
    </div>

    <!-- Legal Declaration Section -->
    <div v-if="legalDeclaration" class="legal-declaration">
      <div class="declaration-text">{{ legalDeclaration }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { VueSignaturePad, type SignaturePadMethods } from 'vue-signature-pad'
import Button from 'primevue/button'
import Checkbox from 'primevue/checkbox'

const props = defineProps({
  title: {
    type: String,
    default: 'Please Sign Below',
  },
  subtitle: {
    type: String,
    default: '',
  },
  width: {
    type: String,
    default: '100%',
  },
  height: {
    type: String,
    default: '300px',
  },
  legalDeclaration: {
    type: String,
    default: undefined,
  },
})

const emit = defineEmits(['save', 'cancel'])

const signaturePad = ref<(InstanceType<typeof VueSignaturePad> & SignaturePadMethods) | null>(null)
const declarationAgreed = ref(false)

const options = {
  penColor: 'rgb(0, 0, 0)',
  backgroundColor: 'rgb(248, 249, 250)',
  minWidth: 1,
  maxWidth: 2.5,
  velocityFilterWeight: 0.7,
}

function clear() {
  signaturePad.value?.clearSignature()
}

function cancel() {
  emit('cancel')
}

async function save() {
  if (!signaturePad.value) return

  const { isEmpty, data } = signaturePad.value.saveSignature()

  if (isEmpty) {
    alert('Please provide a signature')
    return
  }

  // If there's a legal declaration, ensure it's been agreed to
  if (props.legalDeclaration && !declarationAgreed.value) {
    alert('You must agree to the legal declaration before signing')
    return
  }

  try {
    // Process the signature to ensure proper aspect ratio
    const processedSignature = await processSignature(data)

    emit('save', {
      dataUrl: processedSignature,
      timestamp: new Date(),
      declarationAgreed: declarationAgreed.value,
    })
  } catch (error) {
    console.error('Error processing signature:', error)
  }
}

// Process signature to ensure proper aspect ratio and trim whitespace
function processSignature(dataUrl: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      // Create a canvas to process the image
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      if (!ctx) {
        throw new Error('Failed to get 2d context')
      }

      // Set dimensions with a reasonable aspect ratio (3:1 width to height)
      const targetWidth = 600
      const targetHeight = 300

      canvas.width = targetWidth
      canvas.height = targetHeight

      // Fill with white background
      ctx.fillStyle = 'white'
      ctx.fillRect(0, 0, targetWidth, targetHeight)

      // Draw the signature centered and scaled to fit
      const scale = Math.min(targetWidth / img.width, targetHeight / img.height)

      const centerX = (targetWidth - img.width * scale) / 2
      const centerY = (targetHeight - img.height * scale) / 2

      ctx.drawImage(img, centerX, centerY, img.width * scale, img.height * scale)

      // Convert back to data URL
      resolve(canvas.toDataURL('image/png'))
    }
    img.src = dataUrl
  })
}

onMounted(() => {
  // Ensure the signature pad is properly sized
  window.addEventListener('resize', () => {
    signaturePad.value?.resizeCanvas()
  })
})
</script>

<style scoped>
.signature-capture-container {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 1rem;
  overflow-y: auto;
}

.signature-header {
  text-align: center;
  margin-bottom: 1rem;
}

.signature-header h2 {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.subtitle {
  color: #666;
  margin-top: 0.5rem;
}

/* Legal declaration styling */
.legal-declaration {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
}

/* Dark mode for legal declaration */
@media (prefers-color-scheme: dark) {
  .legal-declaration {
    background-color: #1f2937;
    border-color: #374151;
    color: #f9fafb;
  }
}

.declaration-text {
  font-size: 1rem;
  line-height: 1.5;
  white-space: pre-line;
  margin-bottom: 1rem;
}

.declaration-checkbox {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.declaration-checkbox label {
  font-weight: bold;
  font-size: 0.9rem;
}

.sign-here-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.sign-here-text {
  font-size: 1.2rem;
  font-weight: bold;
  color: #333;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

/* Dark mode for SIGN HERE text */
@media (prefers-color-scheme: dark) {
  .sign-here-text {
    color: #f9fafb;
  }
}

.chevron-icon {
  font-size: 1rem;
  color: #666;
  animation: bounce 2s infinite;
}

.signature-pad-container {
  flex: 1;
  max-height: 300px;
  border: 1px solid #ddd;
  outline: 1px solid rgba(0, 109, 0, 0.581);
  border-radius: 4px;
  background-color: rgb(248, 249, 250);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.signature-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.action-left {
  flex: 1;
}

.action-right {
  display: flex;
  gap: 0.5rem;
}

.declaration-checkbox {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.declaration-checkbox label {
  font-weight: bold;
  font-size: 0.9rem;
}

/* Fullscreen mode styles */
@media screen and (max-width: 768px) {
  .signature-capture-container {
    padding: 0.5rem;
  }

  .signature-pad-container {
    border-radius: 0;
  }

  .legal-declaration {
    padding: 0.75rem;
  }

  .declaration-text {
    font-size: 0.9rem;
  }
}

@keyframes bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
}
</style>
